import { byId, clear, el, setBusy, setHidden, setText } from "../dom";
import { formatRelative } from "../format";
import type { Device, FleetResponse, IssuedDevice } from "../types";
import type { LoadStatus } from "./operation";
import { emptyState, errorState, skeletonRows } from "./states";

export interface DeviceForm {
  vehicleId: string;
  routeId: string;
  label: string;
}

export interface DevicesState {
  status: LoadStatus;
  devices: readonly Device[];
  error: string | null;
}

export interface DevicesHandlers {
  onReload(): void;
  onCreate(form: DeviceForm): void;
  onRevoke(device: Device): void;
  onCopyKey(): void;
}

export interface DevicesController {
  setFleet(fleet: FleetResponse | null): void;
  setEndpoint(baseUrl: string): void;
  setState(state: DevicesState): void;
  setCreating(busy: boolean): void;
  setIssued(issued: IssuedDevice | null): void;
  form(): DeviceForm;
}

function fillSelect(select: HTMLSelectElement, options: readonly { value: string; label: string }[]): void {
  const previous = select.value;
  clear(select);
  if (options.length === 0) {
    const placeholder = el("option", { text: "Sin opciones" });
    placeholder.value = "";
    select.append(placeholder);
    select.disabled = true;
    return;
  }
  for (const option of options) {
    const element = el("option", { text: option.label });
    element.value = option.value;
    select.append(element);
  }
  select.disabled = false;
  if (options.some((option) => option.value === previous)) select.value = previous;
}

function vehicleLabel(fleet: FleetResponse | null, vehicleId: string): string {
  const vehicle = fleet?.vehicles.find((item) => item.id === vehicleId);
  return vehicle === undefined ? vehicleId : `${vehicle.label} · ${vehicle.plate}`;
}

function routeLabel(fleet: FleetResponse | null, routeId: string): string {
  return fleet?.routes.find((item) => item.id === routeId)?.name ?? routeId;
}

export function initDevices(handlers: DevicesHandlers): DevicesController {
  const summary = byId<HTMLParagraphElement>("devices-summary");
  const reloadButton = byId<HTMLButtonElement>("reload-devices");
  const vehicleSelect = byId<HTMLSelectElement>("device-vehicle");
  const routeSelect = byId<HTMLSelectElement>("device-route");
  const labelInput = byId<HTMLInputElement>("device-label");
  const createButton = byId<HTMLButtonElement>("device-create");
  const keyBox = byId<HTMLDivElement>("device-key-box");
  const keyValue = byId<HTMLParagraphElement>("device-key-value");
  const keyOwner = byId<HTMLParagraphElement>("device-key-owner");
  const keyUrl = byId<HTMLElement>("device-key-url");
  const keyCopy = byId<HTMLButtonElement>("device-key-copy");
  const listElement = byId<HTMLUListElement>("device-list");
  const placeholder = byId<HTMLParagraphElement>("device-placeholder");

  const errorSlot = el("div", { className: "operation__error" });
  createButton.insertAdjacentElement("beforebegin", errorSlot);

  let fleet: FleetResponse | null = null;
  let devices: readonly Device[] = [];
  let status: LoadStatus = "idle";
  let error: string | null = null;
  let issued: IssuedDevice | null = null;

  function canCreate(): boolean {
    return status !== "loading" && vehicleSelect.value !== "" && routeSelect.value !== "";
  }

  function render(): void {
    setBusy(reloadButton, status === "loading");
    clear(errorSlot);
    renderKey();
    renderList();

    const active = devices.filter((device) => device.revoked_at === undefined).length;
    if (status === "idle" || status === "loading") {
      setText(summary, "Cargando rastreadores…");
    } else if (status === "error") {
      setText(summary, "Rastreadores sin cargar");
    } else if (devices.length === 0) {
      setText(summary, "Sin rastreadores registrados");
    } else {
      setText(summary, `${devices.length} ${devices.length === 1 ? "equipo" : "equipos"} · ${active} activos`);
    }

    createButton.disabled = !canCreate();
    setHidden(placeholder, issued !== null || devices.length > 0);
  }

  function renderKey(): void {
    if (issued === null) {
      setHidden(keyBox, true);
      return;
    }
    setHidden(keyBox, false);
    setText(keyValue, issued.device_key);
    setText(
      keyOwner,
      `${issued.vehicle.label} · ${issued.vehicle.plate} · ${issued.route.name}` +
        (issued.label.trim() === "" ? "" : ` · ${issued.label}`),
    );
  }

  function renderList(): void {
    clear(listElement);

    if (status === "idle" || status === "loading") {
      listElement.append(skeletonRows(2));
      return;
    }
    if (status === "error") {
      listElement.append(
        errorState({
          title: "No pudimos cargar los rastreadores",
          message: error ?? "Revisa la conexión e intenta de nuevo.",
          onRetry: handlers.onReload,
        }),
      );
      return;
    }
    if (devices.length === 0) {
      listElement.append(
        emptyState({
          iconName: "i-pin",
          title: "Sin rastreadores",
          message:
            "Registra el GPS físico de un vehículo para seguir reportando donde el teléfono del conductor no tiene señal.",
        }),
      );
      return;
    }

    const now = Date.now();
    for (const device of devices) {
      const revoked = device.revoked_at !== undefined;
      const item = el("li", { className: "alert" });
      const head = el("div", { className: "alert__head" });
      head.append(
        el("span", {
          className: "alert__title",
          text: device.label.trim() === "" ? vehicleLabel(fleet, device.vehicle_id) : device.label,
          title: device.id,
        }),
        el("span", {
          className: "alert__time",
          text: device.last_seen_at === undefined ? "Sin señal aún" : formatRelative(device.last_seen_at, now),
        }),
      );

      const chips = el("div", { className: "alert__chips" });
      chips.append(
        el("span", {
          className: revoked ? "chip chip--warn" : "chip chip--ok",
          text: revoked ? "Revocado" : "Activo",
        }),
      );

      item.append(
        head,
        el("p", {
          className: "alert__route",
          text: `${vehicleLabel(fleet, device.vehicle_id)} · ${routeLabel(fleet, device.route_id)}`,
        }),
        chips,
      );

      if (!revoked) {
        const actions = el("div", { className: "device__actions" });
        const revoke = el("button", { className: "btn btn--danger btn--sm", text: "Revocar equipo" });
        revoke.type = "button";
        revoke.addEventListener("click", () => handlers.onRevoke(device));
        actions.append(revoke);
        item.append(actions);
      }

      listElement.append(item);
    }
  }

  reloadButton.addEventListener("click", handlers.onReload);
  keyCopy.addEventListener("click", handlers.onCopyKey);
  createButton.addEventListener("click", () => {
    const form = {
      vehicleId: vehicleSelect.value,
      routeId: routeSelect.value,
      label: labelInput.value,
    };
    if (form.vehicleId === "" || form.routeId === "") return;
    handlers.onCreate(form);
  });

  return {
    setFleet(nextFleet) {
      fleet = nextFleet;
      if (nextFleet === null) {
        fillSelect(vehicleSelect, []);
        fillSelect(routeSelect, []);
      } else {
        fillSelect(
          vehicleSelect,
          nextFleet.vehicles.map((vehicle) => ({ value: vehicle.id, label: `${vehicle.label} · ${vehicle.plate}` })),
        );
        fillSelect(
          routeSelect,
          nextFleet.routes.map((route) => ({ value: route.id, label: route.name })),
        );
      }
      render();
    },

    setEndpoint(baseUrl) {
      keyUrl.textContent = `${baseUrl.replace(/\/+$/, "")}/v1/ingest/telemetry`;
    },

    setState(next) {
      status = next.status;
      devices = next.devices;
      error = next.error;
      render();
    },

    setCreating(busy) {
      setBusy(createButton, busy);
      setText(createButton, busy ? "Generando clave…" : issued === null ? "Generar clave del rastreador" : "Generar otra clave");
    },

    setIssued(next) {
      issued = next;
      if (next !== null) {
        labelInput.value = "";
        setText(createButton, "Generar otra clave");
      } else {
        setText(createButton, "Generar clave del rastreador");
      }
      render();
    },

    form() {
      return {
        vehicleId: vehicleSelect.value,
        routeId: routeSelect.value,
        label: labelInput.value,
      };
    },
  };
}
